import { useState, ReactNode, memo, useEffect } from "react";
import Twemoji from "react-twemoji";

import "./BigBoardPage.css";

import { arrayZipMap } from "util/util";
import settings from "settings";
import { usePinnedTeams } from "stores/BrowserWidePersistentState";
import { useGlobalScopeState, useTeamScopeState } from "stores/ScopeStateStore";
import { useIsAdmin } from "stores/ServerInteractionStore";
import { useBigBoardPreferencesStore } from "stores/UserPreferencesStore";
import { useNavStore, useRedirectIfNonAdmin } from "stores/NavStore";
import { PuzzleData, PuzzleType } from "sockapi/puzzle-data";
import { ScopeType } from "sockapi/scope-types";
import { SockAPIScopeState } from "sockapi/scope-state";
import { BigBoardTeamPuzzleState } from "sockapi/scope-specs/big-board-spec";
import {
  getNumTotalHintTokens,
  isPuzzleEligibleForHint,
} from "sockapi/scope-specs/team-hints-spec";
import { TeamSummaryState } from "sockapi/scope-specs/team-list-spec";

import { LargeSpinner } from "components/Loading";
import { HackyAdminPinButton } from "pages/AdminPage";
import { useSortedTeams } from "pages/TeamListPage";
import { Link, PuzzleLink } from "components/Link";
import { TeamDisplayNamePreloaded } from "components/TeamDisplayName";
import Tooltip from "components/Tooltip";
import PageChanger from "components/PageChanger";
import { ShortDate } from "components/dates";
import { normalizeForSearch } from "components/PuzzlePage/Sidebar";
import { Approachability } from "components/Approachability";

export type BigBoardColumnData = {
  colKey: string;
  puzName?: string;
  puzReadable?: string;
  slug?: string;
  displayName: string;
  shortName?: string;
  isMeta?: boolean;
};

const makeShortName = (displayName: string) => {
  const ret = displayName.match(/[^\s-]*\w[^\s-]*/g)?.map((w) => w.match(/\w/));
  if (!ret) return "???";
  /*
  const ret = [];
  let isLastAlpha = false;
  for (const c of displayName) {
    if (c === "'") continue;
    const isAlpha = c.match(/^[\p{L}’]$/u) !== null;
    if (isAlpha ? !isLastAlpha : c !== " ") ret.push(c);
    isLastAlpha = isAlpha;
  }
  */
  if (ret.length >= 6) return `${ret.slice(0, 4).join("")}…`;
  else return ret.join("");
};

const makePuzColData = (
  puzzle: PuzzleData,
  reactKeyPrefix?: string
): BigBoardColumnData => {
  const { puzName, puzReadable, slug, displayName, isMeta } = puzzle;
  return {
    colKey: `${reactKeyPrefix ?? "puz"}-${puzName}`,
    puzName,
    puzReadable,
    slug,
    displayName,
    isMeta,
  };
};

const makePuzListColData = (
  puzzles: PuzzleData[],
  reactKeyPrefix?: string
): BigBoardColumnData[] => {
  return puzzles.map((puzzle) => makePuzColData(puzzle, reactKeyPrefix));
};

const makeBigBoardColGroup = (cols: BigBoardColumnData[]) => {
  return cols.map((colData, i) => (
    <col
      key={colData.colKey}
      className={i === cols.length - 1 ? "big-board-group-end" : ""}
    />
  ));
};

export function TeamListTeamName({ teamId }: { teamId: string }) {
  const teams = useGlobalScopeState(ScopeType.TEAM_LIST) ?? {};
  return (
    <span title={teams[teamId]?.displayName}>
      <TeamDisplayNamePreloaded
        teamData={teams[teamId]}
        hideAdminLinks={true}
      />
    </span>
  );
}

function BigBoardCheckboxOption({
  desc,
  state,
  onChange,
}: {
  desc: ReactNode;
  state: boolean;
  onChange: (newState: boolean) => void;
}) {
  return (
    <label className="big-board-checkbox-option">
      <input
        type="checkbox"
        defaultChecked={state}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />{" "}
      {desc}
    </label>
  );
}

export function BigBoardTeamSearch() {
  const setBigBoardTeamFilter = useNavStore(
    (state) => state.setBigBoardTeamFilter
  );
  return (
    <label className="big-board-team-search">
      <Twemoji>🔍</Twemoji>{" "}
      <input
        placeholder="Filter teams..."
        onChange={(e) =>
          setBigBoardTeamFilter(e.target.value.toLowerCase().trim())
        }
      />
    </label>
  );
}

function BigBoardPuzzleSearch() {
  const setBigBoardPuzzleFilter = useNavStore(
    (state) => state.setBigBoardPuzzleFilter
  );
  return (
    <label className="big-board-team-search">
      <Twemoji>🔍</Twemoji>{" "}
      <input
        placeholder="Filter puzzles..."
        onChange={(e) =>
          setBigBoardPuzzleFilter(e.target.value.toLowerCase().trim())
        }
      />
    </label>
  );
}

export function BigBoardPageSelection() {
  const numTeamsPerPage = useBigBoardPreferencesStore(
    (state) => state.numTeamsPerPage
  );
  const setNumTeamsPerPage = useBigBoardPreferencesStore(
    (state) => state.setNumTeamsPerPage
  );
  const pageNum = useBigBoardPreferencesStore((state) => state.pageNum);
  const setPageNum = useBigBoardPreferencesStore((state) => state.setPageNum);
  const [numTeamsPerPageInput, setNumTeamsPerPageInput] = useState(
    numTeamsPerPage.toString()
  );
  const [pageNumInput, setPageNumInput] = useState((pageNum + 1).toString());
  const teams = useGlobalScopeState(ScopeType.TEAM_LIST) ?? {};
  const numTeams = Object.keys(teams).length;
  return (
    <PageChanger
      numPerPage={numTeamsPerPage}
      setNumPerPage={setNumTeamsPerPage}
      pageNum={pageNum}
      setPageNum={setPageNum}
      numEntries={numTeams}
    />
  );
}

export function BigBoardHeader({ colData }: { colData: BigBoardColumnData }) {
  const { colKey, puzName, puzReadable, displayName, shortName, isMeta } =
    colData;
  const linkChildren = ((): string => {
    if (shortName !== undefined) return shortName;
    if (displayName !== "") return makeShortName(displayName);
    if (puzReadable === undefined) return "???";
    return puzReadable;
  })();
  return (
    <th
      key={colKey}
      className={isMeta ? "puzzle-title-header-meta" : undefined}
    >
      <div className="puzzle-title-header-link">
        {puzName === undefined ? (
          linkChildren
        ) : (
          <Tooltip text={`[${puzReadable}] ${displayName}`} placement="bottom">
            <PuzzleLink puzName={puzName}>{linkChildren}</PuzzleLink>
          </Tooltip>
        )}
      </div>
    </th>
  );
}

export const useCreateBigBoardPageTeamIds = (showHidden: boolean) => {
  const bigBoardTeamFilter = useNavStore((state) => state.bigBoardTeamFilter);
  const sortedTeams = useSortedTeams(showHidden);
  const numTeamsPerPage = useBigBoardPreferencesStore(
    (state) => state.numTeamsPerPage
  );
  const pageNum = useBigBoardPreferencesStore((state) => state.pageNum);
  const pinnedTeams = usePinnedTeams();

  if (sortedTeams === null) return null;

  const defaultSortFunc = (a: string, b: string): number => {
    const isPinnedA = pinnedTeams.includes(a);
    const isPinnedB = pinnedTeams.includes(b);
    if (isPinnedA && isPinnedB)
      return pinnedTeams.indexOf(b) - pinnedTeams.indexOf(a);
    return (isPinnedB ? 1 : 0) - (isPinnedA ? 1 : 0);
  };

  return (sortFunc?: (teamId1: string, teamId2: string) => number) =>
    sortedTeams
      .filter(({ displayName }) =>
        displayName.toLowerCase().includes(bigBoardTeamFilter)
      )
      .map(({ teamId }) => teamId)
      .sort(sortFunc ?? defaultSortFunc)
      .slice(pageNum * numTeamsPerPage, (pageNum + 1) * numTeamsPerPage);
};

type BigBoardPuzzleCellContext = {
  teamPuzState?: BigBoardTeamPuzzleState;
  huntEndTime: number;
  rank?: number;
};

function BigBoardPuzzleCellNoMemo({
  teamPuzState,
  huntEndTime,
  rank,
}: BigBoardPuzzleCellContext) {
  const {
    solveTime,
    numHintsUsed = 0,
    numWrongGuesses = 0,
    approachability,
  } = teamPuzState ?? {};
  const isSolved = solveTime !== undefined;
  const isUnlocked = teamPuzState !== undefined;
  const isPostsolved = solveTime !== undefined && solveTime > huntEndTime;
  // TODO: fill this in once we have a round structure
  const isBacksolved = false;
  const classes = [
    ...(isSolved
      ? ["big-board-solved"]
      : numWrongGuesses > 0
        ? ["big-board-wrong"]
        : isUnlocked
          ? ["big-board-unlocked"]
          : []),
    // Note that this differs from the usual bigboard in that we
    // also count hints that were asked but not answered.
    // I don't think it really makes a big difference since if we
    // don't want a hint to count we'll just refund it soon enough.
    ...(numHintsUsed > 0 ? ["big-board-hinted"] : []),
    ...(isPostsolved ? ["big-board-postsolved"] : []),
    ...(isBacksolved ? ["big-board-backsolved"] : []),
  ];
  const className = classes.join(" ");
  return (
    <td
      className={className === "" ? undefined : className}
      title={
        solveTime === undefined
          ? undefined
          : `${isBacksolved ? "Backsolved" : "Solved"} at ${new Date(
              solveTime
            ).toLocaleString()}`
      }
    >
      {rank === undefined ? (
        approachability === undefined ? null : (
          <Approachability tooltip={false} approachability={approachability} />
        )
      ) : (
        rank + 1
      )}
      {numWrongGuesses === 0 ? null : <small>&minus;{numWrongGuesses}</small>}
      {numHintsUsed === 0 ? null : <small>+{numHintsUsed}</small>}
    </td>
  );
}
const BigBoardPuzzleCell = memo(BigBoardPuzzleCellNoMemo);

type SolveTimeRecord = {
  teamId: string;
  solveTime: number;
};

const computeBigBoardStats = (
  bigBoard: SockAPIScopeState<ScopeType.BIG_BOARD>,
  teams: SockAPIScopeState<ScopeType.TEAM_LIST>,
  sortedTeams: TeamSummaryState[],
  puzzles: { [puzName: string]: PuzzleData }
) => {
  const globalRanks: { [teamId: string]: number } = {};
  const solveTimes: { [puzName: string]: SolveTimeRecord[] } = {};
  const solveTimeRanks: { [teamId: string]: { [puzName: string]: number } } =
    {};
  const puzStats: {
    [puzName: string]: {
      numUnlocks: number;
      numSolves: number;
      numWrongGuesses: number;
      numHintsUsed: number;
    };
  } = {};
  const teamStats: {
    [teamId: string]: {
      lastSolveTime?: number;
      numSolves: number;
      numWrongGuesses: number;
      numHintsUsed: number;
    };
  } = Object.fromEntries(
    Object.keys(bigBoard.teams).map((teamId) => [
      teamId,
      {
        numSolves: 0,
        numWrongGuesses: 0,
        numHintsUsed: 0,
      },
    ])
  );

  for (const [teamId, teamState] of Object.entries(bigBoard.teams)) {
    const { isHidden = false } = teams[teamId];
    const currTeamStats = teamStats[teamId];
    for (const [puzName, puzState] of Object.entries(teamState.unlocks)) {
      const { solveTime, numWrongGuesses = 0, numHintsUsed = 0 } = puzState;
      puzStats[puzName] ??= {
        numUnlocks: 0,
        numSolves: 0,
        numWrongGuesses: 0,
        numHintsUsed: 0,
      };
      const currPuzStats = puzStats[puzName];
      if (solveTime !== undefined) {
        if (!isHidden) {
          currPuzStats.numSolves ??= 0;
          currPuzStats.numSolves++;
          solveTimes[puzName] ??= [];
          solveTimes[puzName].push({ teamId, solveTime });
        }
        if (puzzles[puzName].type === PuzzleType.PUZZLE) {
          currTeamStats.lastSolveTime = Math.max(
            currTeamStats.lastSolveTime ?? 0,
            solveTime
          );
          currTeamStats.numSolves++;
        }
      }
      currTeamStats.numWrongGuesses += numWrongGuesses;
      currTeamStats.numHintsUsed += numHintsUsed;
      if (!isHidden) {
        currPuzStats.numUnlocks++;
        currPuzStats.numWrongGuesses += numWrongGuesses;
        currPuzStats.numHintsUsed += numHintsUsed;
      }
    }
  }
  sortedTeams.forEach(({ teamId }, i) => (globalRanks[teamId] = i));

  for (const [puzName, puzSolveTimes] of Object.entries(solveTimes)) {
    puzSolveTimes.sort(
      (record1, record2) => record1.solveTime - record2.solveTime
    );
    for (const [i, { teamId }] of puzSolveTimes.entries()) {
      solveTimeRanks[teamId] ??= {};
      solveTimeRanks[teamId][puzName] = i;
    }
  }

  return {
    globalRanks,
    solveTimes,
    solveTimeRanks,
    puzStats,
    teamStats,
  };
};

function BigBoardPage() {
  const showHidden = useBigBoardPreferencesStore((state) => state.showHidden);
  const setShowHidden = useBigBoardPreferencesStore(
    (state) => state.setShowHidden
  );
  const teamScopeState = useTeamScopeState();
  const teams = useGlobalScopeState(ScopeType.TEAM_LIST) ?? {};
  const huntAdminState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const isAdmin = useIsAdmin() ?? false;
  const bigBoard = useGlobalScopeState(ScopeType.BIG_BOARD);
  const createTeamIds = useCreateBigBoardPageTeamIds(showHidden);
  const sortedTeams = useSortedTeams(false, true) ?? [];
  const bigBoardPuzzleFilter = useNavStore(
    (state) => state.bigBoardPuzzleFilter
  );

  useRedirectIfNonAdmin();

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(interval);
  }, [now, setNow]);

  // Check that the SERVER scope has been synced by checking
  // that puzzles is non-empty.
  if (
    bigBoard === null ||
    teamScopeState === null ||
    huntAdminState === null ||
    createTeamIds === null
  )
    return <LargeSpinner />;
  const { rounds, puzzles, teamAdminData } = huntAdminState;
  const teamIds = createTeamIds();

  const { huntEndTime } = teamScopeState;

  const { globalRanks, solveTimeRanks, puzStats, teamStats } =
    computeBigBoardStats(bigBoard, teams, sortedTeams, puzzles);

  const puzzleColGroupSpecs: {
    key: string;
    header: string;
    filterFunc: (puz: PuzzleData) => boolean;
  }[] = Object.values(rounds)
    .sort((a, b) => a.order - b.order)
    .map((round) => ({
      key: round.roundId,
      header: round.displayName,
      filterFunc: (puz) =>
        puz.type === PuzzleType.PUZZLE &&
        puz.roundId == round.roundId &&
        normalizeForSearch(
          puz.displayName !== "" ? puz.displayName : puz.puzName
        ).includes(normalizeForSearch(bigBoardPuzzleFilter)),
    }));
  puzzleColGroupSpecs.push({
    key: "story",
    header: "Story beats",
    filterFunc: (puz) => puz.type === PuzzleType.STORY,
  });
  const sortedPuzzleGroups = puzzleColGroupSpecs.map(({ key, filterFunc }) =>
    Object.values(puzzles)
      .filter(filterFunc)
      .sort((puz1, puz2) => puz1.order - puz2.order)
  );
  const sortedPuzzles = sortedPuzzleGroups.flat();

  const puzzleGroupCols = sortedPuzzleGroups.map((puzzleGroup) =>
    makePuzListColData(puzzleGroup)
  );
  const cols: BigBoardColumnData[] = puzzleGroupCols.flat();

  const rowsDom = teamIds.map((teamId) => {
    const { numGrantedHints = 0, unlocks = {} } = bigBoard.teams[teamId] ?? {};
    const {
      lastSolveTime = 0,
      numSolves = 0,
      numWrongGuesses = 0,
      numHintsUsed = 0,
    } = teamStats[teamId] ?? {};
    const globalRank = globalRanks[teamId];
    const adminData =
      !isAdmin || settings.isImpersonate
        ? null
        : (teamAdminData[teamId] ?? null);
    const numHintsTotal = getNumTotalHintTokens(
      settings.isPosthunt,
      numGrantedHints,
      teamScopeState,
      Date.now()
    );
    return (
      <tr key={teamId}>
        <td>
          <TeamListTeamName teamId={teamId} />
        </td>
        <td>
          {adminData === null ? null : (
            <span style={{ display: "flex" }}>
              <Link route="impersonate" params={{ userId: adminData.userId }}>
                <Tooltip text="Impersonate">
                  <Twemoji>🕵️</Twemoji>
                </Tooltip>
              </Link>
              <Link route="sock_admin_team" params={{ teamId }}>
                <Tooltip text="Sock Admin">
                  <Twemoji>🧦️</Twemoji>
                </Tooltip>
              </Link>
              <HackyAdminPinButton pinType="pinnedTeams" entityId={teamId} />
            </span>
          )}
        </td>
        <td>{globalRank !== undefined ? globalRank + 1 : null}</td>
        <td>
          {numSolves <= 0 ? null : numSolves}
          {numWrongGuesses <= 0 ? null : (
            <small>&minus;{numWrongGuesses}</small>
          )}
        </td>
        <td>
          {numHintsUsed <= 0 && numHintsTotal <= 0
            ? null
            : `${numHintsUsed} / ${numHintsTotal}`}
        </td>
        <td>
          {lastSolveTime ? <ShortDate timestamp={lastSolveTime} /> : null}
        </td>
        {sortedPuzzles.map(({ puzName }) => {
          return (
            <BigBoardPuzzleCell
              key={puzName}
              teamPuzState={unlocks[puzName]}
              huntEndTime={huntEndTime}
              rank={solveTimeRanks[teamId]?.[puzName]}
            />
          );
        })}
      </tr>
    );
  });

  const leftCols = [
    <th key="team">Team</th>,
    <th key="impersonate"></th>,
    <th key="rank">#</th>,
    <th key="num-solves">
      <Tooltip text="Solves">
        <Twemoji>✅</Twemoji>
      </Tooltip>
    </th>,
    <th key="num-hints">
      <Tooltip text="Hints">
        <Twemoji>🍞️</Twemoji>
      </Tooltip>
    </th>,
    <th key="last-solve">Last solve</th>,
  ];

  const makePuzStatsRow = (
    rowName: string,
    makeElem: (puzName: string) => ReactNode
  ) => {
    return (
      <tr>
        <td>{rowName}</td>
        <td colSpan={leftCols.length - 1} />
        {sortedPuzzles.map(({ puzName }) => makeElem(puzName))}
      </tr>
    );
  };
  const makePercentage = (num: number, denom: number) => {
    if (num === 0 || denom === 0) return "";
    return `${Math.round((100 * num) / denom)}`;
  };

  return (
    <>
      <div className="big-board">
        <table>
          <colgroup>
            {leftCols.map((col, i) => (
              <col
                key={i}
                className={
                  i === leftCols.length - 1 ? "big-board-group-end" : ""
                }
              />
            ))}
            {puzzleGroupCols.map((puzzleGroup) =>
              makeBigBoardColGroup(puzzleGroup)
            )}
          </colgroup>
          <thead>
            <tr className="big-board-header-groups">
              <th colSpan={leftCols.length} />
              {arrayZipMap(
                puzzleColGroupSpecs,
                sortedPuzzleGroups,
                ({ key, header }, puzzleGroup) =>
                  puzzleGroup.length > 0 ? (
                    <th key={key} colSpan={puzzleGroup.length}>
                      <div className="big-board-header-group">{header}</div>
                    </th>
                  ) : null
              )}
            </tr>
            <tr>
              {leftCols}
              {cols.map((colData) => (
                <BigBoardHeader key={colData.colKey} colData={colData} />
              ))}
            </tr>
          </thead>
          <tbody>
            {makePuzStatsRow("Solves", (puzName) => (
              <td key={puzName} className="big-board-solved">
                {puzStats[puzName]?.numSolves ?? 0}
              </td>
            ))}
            {makePuzStatsRow("Guesses", (puzName) =>
              puzzles[puzName].answer === null ? (
                <td key={puzName} />
              ) : (
                <td key={puzName} className="big-board-wrong">
                  {(puzStats[puzName]?.numWrongGuesses ?? 0) +
                    (puzStats[puzName]?.numSolves ?? 0)}
                </td>
              )
            )}
            {makePuzStatsRow("Unlocks", (puzName) => (
              <td key={puzName} className="big-board-unlocked">
                {puzStats[puzName]?.numUnlocks ?? 0}
              </td>
            ))}
            {makePuzStatsRow("Hints", (puzName) =>
              !isPuzzleEligibleForHint(puzzles[puzName]) ? (
                <td key={puzName} />
              ) : (
                <td key={puzName} className="big-board-hinted">
                  {puzStats[puzName]?.numHintsUsed ?? 0}
                </td>
              )
            )}
            {makePuzStatsRow("%Solve/Guess", (puzName) =>
              puzzles[puzName].answer === null ? (
                <td key={puzName} />
              ) : (
                <td key={puzName}>
                  {makePercentage(
                    puzStats[puzName]?.numSolves ?? 0,
                    (puzStats[puzName]?.numWrongGuesses ?? 0) +
                      (puzStats[puzName]?.numSolves ?? 0)
                  )}
                </td>
              )
            )}
            {makePuzStatsRow("%Solve/Unlock", (puzName) => (
              <td key={puzName}>
                {makePercentage(
                  puzStats[puzName]?.numSolves ?? 0,
                  puzStats[puzName]?.numUnlocks ?? 0
                )}
              </td>
            ))}
            {rowsDom}
          </tbody>
        </table>
        <div className="big-board-right-padding" />
      </div>
      <div className="big-board-footer">
        <BigBoardTeamSearch />
        <BigBoardPageSelection />
        <BigBoardCheckboxOption
          desc=<span className="big-board-show-hidden-teams-label">
            Show hidden teams
          </span>
          state={showHidden}
          onChange={setShowHidden}
        />
        <BigBoardPuzzleSearch />
      </div>
    </>
  );
}

export default BigBoardPage;
